import { Eye, Trash2 } from 'lucide-react';

import { PolicyReviewTableRow } from '@/types/service/policyUserReview';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { ColumnDef } from '@tanstack/react-table';

/**
 * Admin view columns for All Reviews table
 */
export const createAllReviewsColumns = (
  onView: (review: PolicyReviewTableRow) => void,
  onDelete: (review: PolicyReviewTableRow) => void,
): ColumnDef<PolicyReviewTableRow>[] => [
  {
    accessorKey: 'user_email',
    header: 'User',
    cell: ({ row }) => {
      const review = row.original;
      return (
        <div>
          <div className='font-medium'>
            {review.user_first_name} {review.user_last_name}
          </div>
          <div className='text-sm text-muted-foreground'>{review.user_email}</div>
        </div>
      );
    },
  },
  {
    accessorKey: 'group_name',
    header: 'Group',
    cell: ({ row }) => {
      const groupName = row.original.group_name;
      return groupName ? (
        <span className='text-sm'>{groupName}</span>
      ) : (
        <span className='text-sm text-muted-foreground italic'>Ad-hoc</span>
      );
    },
  },
  {
    accessorKey: 'policy_name',
    header: 'Policy',
    cell: ({ row }) => (
      <div className='max-w-xs truncate' title={row.original.policy_name}>
        {row.original.policy_name}
      </div>
    ),
  },
  {
    accessorKey: 'approved',
    header: 'Status',
    cell: ({ row }) => {
      const approved = row.original.approved;
      return approved ? (
        <Badge variant='default' className='bg-green-600'>
          Approved
        </Badge>
      ) : (
        <Badge variant='secondary'>Pending</Badge>
      );
    },
  },
  {
    accessorKey: 'review_period_start',
    header: 'Review Period',
    cell: ({ row }) => {
      const start = new Date(row.original.review_period_start).toLocaleDateString();
      const end = new Date(row.original.review_period_end).toLocaleDateString();
      return (
        <div className='text-sm'>
          {start} - {end}
        </div>
      );
    },
  },
  {
    accessorKey: 'acknowledge_timestamp',
    header: 'Acknowledged',
    cell: ({ row }) => {
      const timestamp = row.original.acknowledge_timestamp;
      return timestamp ? (
        <span className='text-sm'>{new Date(timestamp).toLocaleString()}</span>
      ) : (
        <span className='text-sm text-muted-foreground'>-</span>
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const review = row.original;
      return (
        <div className='flex gap-2'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onView(review)}
            title='View Review'
            className='h-8 w-8 p-0'
          >
            <Eye className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onDelete(review)}
            disabled={review.isDeleting}
            title='Delete Review'
            className='h-8 w-8 p-0 text-destructive hover:text-destructive'
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        </div>
      );
    },
  },
];

/**
 * User view columns for My Reviews table
 */
export const createMyReviewsColumns = (
  onView: (review: PolicyReviewTableRow) => void,
  onAcknowledge: (review: PolicyReviewTableRow) => void,
): ColumnDef<PolicyReviewTableRow>[] => [
  {
    accessorKey: 'policy_name',
    header: 'Policy',
    cell: ({ row }) => (
      <div className='max-w-md'>
        <div className='font-medium'>{row.original.policy_name}</div>
      </div>
    ),
  },
  {
    accessorKey: 'group_name',
    header: 'Group',
    cell: ({ row }) => {
      const groupName = row.original.group_name;
      return groupName ? (
        <span className='text-sm'>{groupName}</span>
      ) : (
        <span className='text-sm text-muted-foreground italic'>Ad-hoc</span>
      );
    },
  },
  {
    accessorKey: 'approved',
    header: 'Status',
    cell: ({ row }) => {
      const approved = row.original.approved;
      return approved ? (
        <Badge variant='default' className='bg-green-600'>
          Approved
        </Badge>
      ) : (
        <Badge variant='secondary'>Pending</Badge>
      );
    },
  },
  {
    accessorKey: 'review_period_start',
    header: 'Review Period',
    cell: ({ row }) => {
      const start = new Date(row.original.review_period_start).toLocaleDateString();
      const end = new Date(row.original.review_period_end).toLocaleDateString();
      return (
        <div className='text-sm'>
          {start} - {end}
        </div>
      );
    },
  },
  {
    accessorKey: 'acknowledge_timestamp',
    header: 'Acknowledged',
    cell: ({ row }) => {
      const timestamp = row.original.acknowledge_timestamp;
      return timestamp ? (
        <span className='text-sm'>{new Date(timestamp).toLocaleString()}</span>
      ) : (
        <span className='text-sm text-muted-foreground'>-</span>
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const review = row.original;
      const isApproved = review.approved;

      return (
        <div className='flex gap-2'>
          <Button variant='ghost' size='sm' onClick={() => onView(review)} title='View Review'>
            <Eye className='h-4 w-4 mr-2' />
            View
          </Button>
          {!isApproved && (
            <Button
              variant='default'
              size='sm'
              onClick={() => onAcknowledge(review)}
              disabled={review.isAcknowledging}
              className='bg-green-600 hover:bg-green-700'
            >
              {review.isAcknowledging ? 'Acknowledging...' : 'Acknowledge'}
            </Button>
          )}
        </div>
      );
    },
  },
];
